import {ArtworkGuidelineType} from "../../definitions/enums";
import {GuidelineType} from "../../definitions/types";
import {GuidelineModel} from "../domain/guidelineModel";

export class TemplatesAndGuidelinesModel {
    private readonly _productId: number;
    private readonly _guidelineType: ArtworkGuidelineType;
    private readonly _guidelineTypeId: number | undefined;
    private readonly _templates: GuidelineModel[];

    constructor(public readonly productId: number,
                public readonly type: ArtworkGuidelineType,
                public readonly typeId: number | undefined,
                public readonly media: {
                    guidelines: {
                        artworkTemplate: GuidelineType[]
                    }
                },
                public readonly primaryMedia: {
                    guidelines: {
                        artworkTemplate: GuidelineType[]
                    }
                }) {
        this._productId = productId;
        this._guidelineType = type;
        this._guidelineTypeId = typeId;
        this._templates = [
            ...media.guidelines.artworkTemplate.map(guideline => new GuidelineModel(guideline)),
            ...primaryMedia.guidelines.artworkTemplate.map(guideline => new GuidelineModel(guideline))
        ].filter((template, index, self) => index === self.findIndex((t) => t.url === template.url));
    }

    /**
     * @method guidelineType
     * @description Returns the guideline type for the templates and guidelines section
     * @returns {ArtworkGuidelineType}
     */
    public get guidelineType(): ArtworkGuidelineType {
        return this._guidelineType;
    }

    /**
     * @method href
     * @description Returns the href for the templates and guidelines section
     * @returns {string}
     */
    public get href(): string {
        return `/ArtworkGuideline/${this.guidelineTypeId}/${this._productId}`
    }

    /**
     * @method guidelineTypeId
     * @description Returns the guideline type id
     * @returns {number | undefined}
     */
    public get guidelineTypeId(): number | undefined {
        return this._guidelineTypeId;
    }

    /**
     * @method templates
     * @description Returns the templates for the product and primary media
     * @returns {GuidelineModel[]}
     */
    public get templates(): GuidelineModel[] {
        return this._templates;
    }

    /**
     * @method hasTemplates
     * @description Returns true if there are templates
     * @returns {boolean}
     */
    public get hasTemplates(): boolean {
        return this._templates.length > 0;
    }

    /**
     * @method count
     * @description Returns the number of templates
     * @returns {number}
     */
    public get count(): number {
        return this._templates.length;
    }

    /**
     * @method title
     * @description Returns the title for the templates and guidelines section
     * @returns {string}
     */
    public get title(): string {
        let title = 'Artwork';

        if (this._guidelineType !== ArtworkGuidelineType.None && this.hasTemplates) {
            title += ' Templates & Guidelines';
        } else if (this._guidelineType !== ArtworkGuidelineType.None) {
            title += ' Templates';
        } else {
            title += ' Guidelines';
        }

        return title;
    }

    /**
     * @method isTextOnly
     * @description Returns true if the guideline type is TextOnly
     * @returns {boolean}
     */
    public get isTextOnly(): boolean {
        return this._guidelineType === ArtworkGuidelineType.TextOnly;
    }
}